// ============================================================================
// CLIENT REGISTRATION SERVICE
// ============================================================================
// Business logic and HTTP calls for client statutory registrations
// (prima.tbl_client_registration via /api/v1/ClientRegistrations).
//
// Backend wraps every response in ApiResponse<T>; list endpoints additionally
// wrap data in PagedResult<T>. `getAll()` transforms PagedResult<T> into the
// PaginatedResponse<T> that SyutGrid consumes — see CLAUDE.md
// "Service layer transformation".

import { get, post, put, del } from '../client/axiosClient';
import { clientRegistrationEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  ClientRegistration,
  CreateClientRegistrationRequest,
  UpdateClientRegistrationRequest,
  ClientRegistrationSearchRequest,
} from '../types';

class ClientRegistrationService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: ClientRegistrationSearchRequest
  ): Promise<PaginatedResponse<ClientRegistration>> {
    // Use raw BASE_PATH (NOT clientRegistrationEndpoints.list()) —
    // buildEndpointWithQuery adds the /v1 prefix internally.
    const endpoint = params
      ? buildEndpointWithQuery(clientRegistrationEndpoints.basePath(), params as Record<string, any>)
      : clientRegistrationEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data; // ApiResponse<PagedResult<ClientRegistration>>
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<ClientRegistration>> {
    const response = await get<ApiResponse<ClientRegistration>>(
      clientRegistrationEndpoints.getById(id)
    );
    return response.data;
  }

  async create(
    data: CreateClientRegistrationRequest
  ): Promise<ApiResponse<ClientRegistration>> {
    const response = await post<ApiResponse<ClientRegistration>>(
      clientRegistrationEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateClientRegistrationRequest
  ): Promise<ApiResponse<ClientRegistration>> {
    const response = await put<ApiResponse<ClientRegistration>>(
      clientRegistrationEndpoints.update(id),
      data
    );
    return response.data;
  }

  /** DELETE returns 204 No Content (soft-delete on the server). */
  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(clientRegistrationEndpoints.delete(id));
    return { success: true };
  }
}

export const clientRegistrationService = new ClientRegistrationService();
export default clientRegistrationService;
